import * as React from "react"
import { XIcon } from "./icons"
import { Dialog as SheetPrimitive } from "radix-ui"

import { cn } from "./utils"
import { Elevated } from "./elevated"

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
 return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({
 ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
 return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({
 ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
 return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({
 ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
 return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({
 className,
 ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
 return (
 <SheetPrimitive.Overlay
 data-slot="sheet-overlay"
 className={cn(
 // z-[80]: same modal layer as Dialog — above AppsStage (z-[60]) so sheets
 // opened from Stage content (Chat/Play) are visible. See packages/ui/src/dialog.tsx.
 "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[80] bg-black/40 backdrop-blur-md",
 className
 )}
 {...props}
 />
 )
}

function SheetContent({
 className,
 children,
 side = "right",
 showCloseButton = true,
 ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
 side?: "top" | "right" | "bottom" | "left"
 showCloseButton?: boolean
}) {
 return (
 <SheetPortal>
 <SheetOverlay />
 {/* Substrate offset of 1 lands the sheet panel on surface-2 (bg-card),
  the same substrate as the page panel beneath it. shadowLevel stays
  heavy so the panel still reads as an overlay despite the matching
  background. */}
 <Elevated offset={1} shadowLevel={6} asChild>
 <SheetPrimitive.Content
 data-slot="sheet-content"
 className={cn(
 "data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-[80] flex flex-col gap-4 transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
 side === "right" &&
 "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 sm:max-w-sm",
 side === "left" &&
 "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 sm:max-w-sm",
 side === "top" &&
 "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto",
 side === "bottom" &&
 "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto",
 className
 )}
 {...props}
 >
 {children}
 {showCloseButton && (
 <SheetPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
 <XIcon className="size-4" />
 <span className="sr-only">Close</span>
 </SheetPrimitive.Close>
 )}
 </SheetPrimitive.Content>
 </Elevated>
 </SheetPortal>
 )
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
 return (
 <div
 data-slot="sheet-header"
 className={cn("flex flex-col gap-1.5 p-4", className)}
 {...props}
 />
 )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
 return (
 <div
 data-slot="sheet-footer"
 className={cn("mt-auto flex flex-col gap-2 p-4", className)}
 {...props}
 />
 )
}

function SheetTitle({
 className,
 ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
 return (
 <SheetPrimitive.Title
 data-slot="sheet-title"
 className={cn("type-title text-foreground", className)}
 {...props}
 />
 )
}

function SheetDescription({
 className,
 ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
 return (
 <SheetPrimitive.Description
 data-slot="sheet-description"
 className={cn("type-subtext", className)}
 {...props}
 />
 )
}

export {
 Sheet,
 SheetTrigger,
 SheetClose,
 SheetContent,
 SheetHeader,
 SheetFooter,
 SheetTitle,
 SheetDescription,
}
